import { ContextCollectionState } from '@electron/shared/types';
import { useEffect, useRef } from 'react';

import { useBoundedRetry } from '@/hooks/useBoundedRetry';
import { useCallbackRef } from '@/hooks/useCallbackRef';
import { logger } from '@/lib/logger';

import { useElectronContextCollection } from './context-collection-provider';

export const ERROR_RECOVERY_MAX_ATTEMPTS = 5;
export const ERROR_RECOVERY_BASE_DELAY_MS = 10_000;
export const ERROR_RECOVERY_MAX_DELAY_MS = 60_000;

// Decides when the app may start context collection on its own (manual tray
// start/stop is unaffected). Per observer status:
//   STOPPED, first time this launch → start once
//   ERROR                           → backed-off retries until the budget runs out
//   RUNNING                         → recovered: full retry budget again
//   PAUSED / STOPPING / DISABLED   → drop any pending retry
//   login change                    → reset everything for the new user
// Bounded so a flapping observer can't fire starts in a loop (FE-2438)
export function useAutoStartContextCollection(userEmail: string | undefined) {
  const { startContextCollection, status } = useElectronContextCollection();

  // useCallbackRef returns a stable identity, so listing `start` in the deps
  // can never re-fire the effect — only status.state and userEmail do
  const start = useCallbackRef(startContextCollection);

  const errorRecovery = useBoundedRetry({
    action: () => {
      start().catch((error) => {
        logger.error('Context collection auto-retry start failed', { error });
      });
    },
    maxAttempts: ERROR_RECOVERY_MAX_ATTEMPTS,
    baseDelayMs: ERROR_RECOVERY_BASE_DELAY_MS,
    capDelayMs: ERROR_RECOVERY_MAX_DELAY_MS,
    onExhausted: () => {
      logger.warn(
        'Context collection auto-start retries exhausted; leaving recovery to the main process'
      );
    },
  });

  const hasAttemptedLaunchAutoStartRef = useRef(false);
  const lastUserEmailRef = useRef(userEmail);

  useEffect(() => {
    if (userEmail !== lastUserEmailRef.current) {
      lastUserEmailRef.current = userEmail;
      errorRecovery.reset();
      hasAttemptedLaunchAutoStartRef.current = false;
    }

    if (!userEmail) return;

    switch (status.state) {
      case ContextCollectionState.RUNNING:
        errorRecovery.reset();
        break;

      case ContextCollectionState.STOPPED:
        // A stop invalidates any retry scheduled by an earlier ERROR —
        // auto-start must never undo a user's stop
        errorRecovery.cancel();
        if (!hasAttemptedLaunchAutoStartRef.current) {
          hasAttemptedLaunchAutoStartRef.current = true;
          start().catch((error) => {
            logger.error('Context collection auto-start failed', { error });
          });
        }
        break;

      case ContextCollectionState.PAUSED:
      case ContextCollectionState.STOPPING:
      case ContextCollectionState.DISABLED:
        errorRecovery.cancel();
        break;

      case ContextCollectionState.ERROR:
        errorRecovery.schedule();
        break;

      default:
        break;
    }
  }, [errorRecovery, start, status.state, userEmail]);
}
